<script setup>
import { useShortLink } from '@frontJs/composables/shortLink';
import ArrowSquareOutIcon from '@iconify-vue/ph/arrow-square-out';
import DownloadSimpleIcon from '@iconify-vue/ph/download-simple';
import LinkIcon from '@iconify-vue/ph/link';
import UploadSimpleIcon from '@iconify-vue/ph/upload-simple';
import { router } from '@inertiajs/vue3';
import AlertBox from '@js/components/alerts/AlertBox.vue';
import ActionButton from '@js/components/buttons/ActionButton.vue';
import FilePondField from '@js/components/fields/FilePondField.vue';
import InputField from '@js/components/fields/InputField.vue';
import TextareaField from '@js/components/fields/TextareaField.vue';
import FormColumn from '@js/components/forms/FormColumn.vue';
import FormContent from '@js/components/forms/FormContent.vue';
import FormFooter from '@js/components/forms/FormFooter.vue';
import FormGrid from '@js/components/forms/FormGrid.vue';
import FormLegend from '@js/components/forms/FormLegend.vue';
import FormRoot from '@js/components/forms/FormRoot.vue';
import FormTitle from '@js/components/forms/FormTitle.vue';
import SortableList from '@js/components/lists/SortableList.vue';
import { toDateInput } from '@js/utils/date';
import { useRoute } from '@js/utils/route';
import { isHttpUrl } from '@js/utils/string';
import { toastError, toastSaved, toastSuccess } from '@js/utils/toast';
import { computed, reactive, ref, useTemplateRef } from 'vue';
import { object, string } from 'yup';

const props = defineProps({
    tenant: {
        type: Object,
        required: true,
    },
    isPlatformConfigured: {
        type: Boolean,
        required: false,
    },
    maxContentLength: {
        type: Number,
        required: true,
    },
    maxMediaCount: {
        type: Number,
        required: true,
    },
    defaultScheduledDate: {
        type: String,
        required: false,
    },
    defaultPublicationTime: {
        type: String,
        required: true,
    },
});

const { route } = useRoute();

const { shortenFieldsUrls } = useShortLink();

const shorteningUid = ref(null);

const mediaUrlImportEndpoint = route(
    'front.projectManagement.smo.postMediaFromUrl',
);

const isSubmitting = ref(false);

let uidCounter = 0;

function nextUid() {
    return `${++uidCounter}`;
}

const form = reactive({
    posts: [],
});

/**
 * A new row takes the previous row's date shifted by one day, which saves retyping a whole date
 * on every addition. Its time is carried over as is, the first row starting on the usual one.
 */
function getNextSchedule() {
    const previous = form.posts.at(-1);
    const time = previous?.scheduledTime || props.defaultPublicationTime;

    // The first row starts on the date of the last scheduled post, the series carrying on.
    const fallback = { date: props.defaultScheduledDate ?? '', time };

    if (!previous?.scheduledDate) {
        return fallback;
    }

    const date = new Date(`${previous.scheduledDate}T00:00:00`);

    if (Number.isNaN(date.getTime())) {
        return fallback;
    }

    date.setDate(date.getDate() + 1);

    return { date: toDateInput(date), time };
}

function newPost() {
    const schedule = getNextSchedule();

    return {
        content: '',
        linkUrl: props.tenant.url ?? '',
        scheduledDate: schedule.date,
        scheduledTime: schedule.time,
        medias: [],
        // Addresses coming from an import, downloaded by the field as soon as it appears.
        imageUrls: [],
        _uid: nextUid(),
    };
}

form.posts.push(newPost());

const validationSchema = computed(() => {
    const dynamicSchema = {};

    form.posts.forEach((post) => {
        dynamicSchema[`content_${post._uid}`] = string()
            .required('Ce champ est obligatoire.')
            .max(
                props.maxContentLength,
                `Le contenu ne peut pas dépasser ${props.maxContentLength} caractères.`,
            );
        dynamicSchema[`scheduledDate_${post._uid}`] = string().required(
            'Ce champ est obligatoire.',
        );
        dynamicSchema[`scheduledTime_${post._uid}`] = string().required(
            'Ce champ est obligatoire.',
        );
        dynamicSchema[`linkUrl_${post._uid}`] = string()
            .url('Veuillez saisir une URL valide.')
            .nullable();
    });

    return object(dynamicSchema);
});

const uploadingUids = ref([]);

function setUploading(uid, isUploadingRow) {
    uploadingUids.value = isUploadingRow
        ? [...new Set([...uploadingUids.value, uid])]
        : uploadingUids.value.filter((item) => item !== uid);
}

const isUploading = computed(() =>
    form.posts.some((post) => uploadingUids.value.includes(post._uid)),
);

const isDisabled = computed(
    () =>
        isSubmitting.value ||
        isUploading.value ||
        !props.isPlatformConfigured ||
        form.posts.length === 0,
);

const importInput = useTemplateRef('importInput');

const isImporting = ref(false);

const importErrors = ref([]);

/**
 * Rows read from an editorial calendar are added to the form rather than saved straight away:
 * a spreadsheet written by hand deserves a look before five weeks of publications are created.
 * Each row's visual is downloaded by its own field, from the address the file carries.
 */
function importCsv(event) {
    const file = event.target.files?.[0];

    if (!file) {
        return;
    }

    const data = new FormData();

    data.append('file', file);

    isImporting.value = true;
    importErrors.value = [];

    axios
        .post(route('front.projectManagement.smo.postImportPosts'), data)
        .then((response) => {
            const imported = response.data.posts.map((post) => ({
                content: post.content,
                // Without a link of its own, a row falls back on the customer's website.
                linkUrl: post.linkUrl ?? props.tenant.url ?? '',
                scheduledDate: post.scheduledDate,
                scheduledTime: props.defaultPublicationTime,
                medias: [],
                imageUrls: post.imageUrls,
                _uid: nextUid(),
            }));

            // An untouched starting row would otherwise be saved as an empty publication.
            form.posts = [
                ...form.posts.filter((post) => post.content),
                ...imported,
            ];

            importErrors.value = response.data.errors;

            toastSuccess(`${imported.length} publication(s) importée(s).`);
        })
        .catch((error) => {
            toastError(error, "Le fichier n'a pas pu être importé.");
        })
        .finally(() => {
            isImporting.value = false;
            // Reset so that importing the same file twice in a row still fires the change event.
            event.target.value = '';
        });
}

async function shortenLinks(post) {
    shorteningUid.value = post._uid;

    try {
        await shortenFieldsUrls([
            {
                getValue: () => post.content,
                setValue: (value) => (post.content = value),
            },
            {
                getValue: () => post.linkUrl,
                setValue: (value) => (post.linkUrl = value),
            },
        ]);
    } finally {
        shorteningUid.value = null;
    }
}

function save(status) {
    isSubmitting.value = true;

    axios
        .post(
            route('front.projectManagement.smo.postBatchPosts', {
                tenant: props.tenant.id,
            }),
            {
                posts: form.posts.map((post) => ({
                    content: post.content,
                    linkUrl: post.linkUrl || null,
                    scheduledAt: `${post.scheduledDate} ${post.scheduledTime}`,
                    status: status,
                    medias: post.medias,
                })),
            },
        )
        .then(() => {
            toastSaved();

            router.visit(
                route('front.projectManagement.smo.posts', {
                    tenant: props.tenant.id,
                }),
            );
        })
        .catch((error) => {
            toastError(error);
        })
        .finally(() => {
            isSubmitting.value = false;
        });
}

function submit() {
    save('scheduled');
}
</script>

<template>
    <FormRoot
        :validation-schema="validationSchema"
        :submit="submit"
        v-slot="{ errors }"
    >
        <FormColumn>
            <AlertBox v-if="!props.isPlatformConfigured" type="warning">
                Le service externe Facebook n'est pas configuré pour ce client :
                les publications ne pourront pas être enregistrées.
            </AlertBox>

            <FormContent>
                <FormTitle>
                    {{ form.posts.length }} publication(s) à créer
                </FormTitle>

                <FormLegend>
                    Chaque ligne donne une publication distincte. L'ordre
                    n'influe pas sur la publication, qui suit la date et l'heure
                    saisies&nbsp;; il ne sert qu'au confort de saisie.
                </FormLegend>

                <div class="flex flex-wrap items-center gap-2">
                    <input
                        ref="importInput"
                        type="file"
                        accept=".csv,text/csv"
                        class="hidden"
                        @change="importCsv"
                    />

                    <ActionButton
                        size="small"
                        type="primary"
                        :left-icon="UploadSimpleIcon"
                        :loading="isImporting"
                        :callback="() => importInput?.click()"
                    >
                        Importer un CSV
                    </ActionButton>

                    <ActionButton
                        size="small"
                        type="secondary"
                        :left-icon="DownloadSimpleIcon"
                        :href="
                            route('front.projectManagement.smo.importTemplate')
                        "
                        download
                    >
                        Télécharger le modèle
                    </ActionButton>
                </div>

                <AlertBox v-if="importErrors.length" type="warning">
                    <ul class="list-inside list-disc">
                        <li v-for="error in importErrors" :key="error">
                            {{ error }}
                        </li>
                    </ul>
                </AlertBox>

                <SortableList
                    v-model="form.posts"
                    :new-item="newPost"
                    add-label="Ajouter une publication"
                    empty-text="Aucune publication à créer. Ajoutez-en une pour commencer."
                    :disabled="isSubmitting"
                >
                    <template #default="{ item }">
                        <div class="flex flex-col gap-4">
                            <TextareaField
                                v-model="item.content"
                                label="Contenu"
                                placeholder="Contenu de la publication"
                                :field-name="`content_${item._uid}`"
                                :field-id="`content_${item._uid}`"
                                :rows="5"
                                :highlight="[
                                    /(#[\p{L}\p{N}_]+)/gu,
                                    /(@[\w.]+)/g,
                                ]"
                                emoji-picker
                                required
                                :legend="`${item.content.length} / ${props.maxContentLength} caractères`"
                            >
                                <template #bottom-left-actions>
                                    <ActionButton
                                        size="small"
                                        type="secondary"
                                        :left-icon="LinkIcon"
                                        :loading="shorteningUid === item._uid"
                                        :callback="() => shortenLinks(item)"
                                    >
                                        Raccourcir les liens
                                    </ActionButton>
                                </template>
                            </TextareaField>

                            <FormGrid :columns="3">
                                <InputField
                                    v-model="item.scheduledDate"
                                    label="Date de publication"
                                    :field-name="`scheduledDate_${item._uid}`"
                                    :field-id="`scheduledDate_${item._uid}`"
                                    type="date"
                                    required
                                />

                                <InputField
                                    v-model="item.scheduledTime"
                                    label="Heure de publication"
                                    :field-name="`scheduledTime_${item._uid}`"
                                    :field-id="`scheduledTime_${item._uid}`"
                                    type="time"
                                    required
                                />

                                <InputField
                                    v-model="item.linkUrl"
                                    label="Lien"
                                    placeholder="https://..."
                                    :field-name="`linkUrl_${item._uid}`"
                                    :field-id="`linkUrl_${item._uid}`"
                                    legend="Optionnel, affiché en aperçu par Facebook"
                                >
                                    <template #inputSuffix>
                                        <ActionButton
                                            size="small"
                                            square
                                            type="transparent"
                                            :icon="ArrowSquareOutIcon"
                                            title="Ouvrir le lien dans un nouvel onglet"
                                            :href="isHttpUrl(item.linkUrl) ? item.linkUrl : undefined"
                                            target="_blank"
                                            :disabled="!isHttpUrl(item.linkUrl)"
                                        />
                                    </template>
                                </InputField>
                            </FormGrid>

                            <FilePondField
                                v-model="item.medias"
                                label="Visuels"
                                :field-name="`medias_${item._uid}`"
                                :field-id="`medias_${item._uid}`"
                                accept="image/jpeg, image/png"
                                :max-files="props.maxMediaCount"
                                max-file-size="5MB"
                                gallery
                                :legend="`${item.medias.length} / ${props.maxMediaCount} visuel(s). JPG et PNG, 5 Mo maximum par fichier.`"
                                :url-import-endpoint="mediaUrlImportEndpoint"
                                :initial-url-imports="item.imageUrls"
                                @processing="setUploading(item._uid, $event)"
                            />
                        </div>
                    </template>
                </SortableList>
            </FormContent>
        </FormColumn>

        <FormFooter
            :is-submitting="isSubmitting"
            :errors="errors"
            :disabled="isDisabled"
            submit-text="Programmer les publications"
            submitting-text="Enregistrement en cours..."
        >
            <template #actions>
                <ActionButton
                    type="secondary"
                    :disabled="isDisabled"
                    @click="save('draft')"
                >
                    Enregistrer en brouillons
                </ActionButton>
            </template>
        </FormFooter>
    </FormRoot>
</template>
